import { type ActionContext, type Store } from "vuex";
import { VuexModule } from "vuex-module-decorators";

import { getCollectionsStore } from "@/store/collections";
import { getFollowerStore } from "@/store/follower";
import { getGiftGuidesStore } from "@/store/gift-guides";
import {
  type GiftGuide,
  type GiftGuideMap,
  type GiftGuideProductsMap,
  type LocalizedCategoriesResponse,
  type UserGiftGuidesResponse,
} from "@/store/gift-guides/types";
import { getLikesStore } from "@/store/likes";
import { getLtksStore } from "@/store/ltks";
import { getMediaObjectsStore } from "@/store/media-objects";
import { getProductDetailsStore } from "@/store/product-details";
import { type ManyProductDetailsResponse } from "@/store/product-details/types";
import { getProductSetsStore } from "@/store/product-sets";
import {
  type ManyProductSetProductsResponse,
  type ManyProductSetsResponse,
  type ProductSetResponse,
} from "@/store/product-sets/types";
import { getProductsStore } from "@/store/products";
import { getProfilesStore } from "@/store/profiles";
import { getRetailerProductsStore } from "@/store/retailer-products";
import { getTrendingPostsStore } from "@/store/trending-posts";
import { getWishlistsStore } from "@/store/wishlists";
import { MINUTE } from "@/utils/enums/time";
import { filterRemoveUndefined } from "@/utils/transform/array";
import mapData from "@/utils/transform/map-data";

import { getSharedWishlistsStore } from "./shared-wishlists";

type MappableResponse =
  | ManyCollectionsResponse
  | ManyFollowsResponse
  | ManyLikesResponse
  | ManyLtksResponse
  | ManyMediaObjectsResponse
  | ManyProductDetailsResponse
  | ManyProductsResponse
  | ManyProfilesResponse
  | ManyWishesResponse
  | OneRetailerProductResponse
  | OneCheckoutResponse
  | LocalizedCategoriesResponse
  | ProductSetResponse
  | ManyProductSetsResponse
  | ManyProductSetProductsResponse
  | ManyCartsResponse
  | OneCartResponse
  | TrendingPostResponse
  | UserGiftGuidesResponse;

function isFollowsResponse(data: MappableResponse): data is ManyFollowsResponse {
  return (data as ManyFollowsResponse).follows !== undefined;
}
function isLikesResponse(data: MappableResponse): data is ManyLikesResponse {
  return (data as ManyLikesResponse).likes !== undefined;
}
function isLtksResponse(data: MappableResponse): data is ManyLtksResponse {
  return (data as ManyLtksResponse).ltks !== undefined;
}
function isMediaObjectsResponse(data: MappableResponse): data is ManyMediaObjectsResponse {
  return (data as ManyMediaObjectsResponse).mediaObjects !== undefined;
}
function isProductDetailsResponse(data: MappableResponse): data is ManyProductDetailsResponse {
  return (data as ManyProductDetailsResponse).productDetails !== undefined;
}
function isRetailerProductResponse(data: MappableResponse): data is OneRetailerProductResponse {
  return (data as OneRetailerProductResponse).retailerProduct !== undefined;
}
function isProductsResponse(data: MappableResponse): data is ManyProductsResponse {
  return (data as ManyProductsResponse).products !== undefined;
}
function isProfilesResponse(data: MappableResponse): data is ManyProfilesResponse {
  return (data as ManyProfilesResponse).profiles !== undefined;
}
function isWishlistResponse(data: MappableResponse): data is ManyWishesResponse {
  return (data as ManyWishesResponse).wishes !== undefined;
}
function isCollectionsResponse(data: MappableResponse): data is ManyCollectionsResponse {
  return (data as ManyCollectionsResponse).collections !== undefined;
}
function isLocalizedCategoryResponse(data: MappableResponse): data is LocalizedCategoriesResponse {
  return (data as LocalizedCategoriesResponse).recipients !== undefined;
}
// product set info
function isProductSetInfoResponse(data: MappableResponse): data is ProductSetResponse {
  return (data as ProductSetResponse).shopProductCollection !== undefined;
}
// product set listing
function isProductSetsResponse(data: MappableResponse): data is ManyProductSetsResponse {
  return (data as ManyProductSetsResponse).shopProductCollections !== undefined;
}
// product set detail
function isProductSetProductsResponse(data: MappableResponse): data is ManyProductSetProductsResponse {
  return (data as ManyProductSetProductsResponse).shopProducts !== undefined;
}
function isTrendingPostsResponse(data: MappableResponse): data is TrendingPostResponse {
  return (
    (data as TrendingPostResponse).posts !== undefined && (data as TrendingPostResponse).mediaObjects !== undefined
  );
}
function isGiftGuidesResponse(data: MappableResponse): data is UserGiftGuidesResponse {
  return (
    (data as UserGiftGuidesResponse).posts !== undefined && (data as UserGiftGuidesResponse).coverPhotos !== undefined
  );
}

const staleModelTimeInMinutes = 5;
export const staleModelTime = staleModelTimeInMinutes * MINUTE;

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function isModelStale
 * @desc     Determines if a given model is stale based on its `fetchedAt` property.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param model - The model to check for staleness.
 *
 * @returns A boolean indicating whether the model is stale or not.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const isModelStale = <T extends CoreModel>(model?: T) => {
  return (model?.fetchedAt ?? -staleModelTime - 1) < Date.now() - staleModelTime;
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function duplicates
 * @desc     Returns a function that checks if a given ID is a duplicate
 *          in an array.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param id - The element to check for duplicates.
 * @param index - The index of the element in the array.
 * @param ids - The array to check for duplicates in.
 *
 * @returns A boolean indicating whether the given element is a duplicate in the array.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
function duplicates<T>(id: T, index: number, ids: T[]) {
  return ids.indexOf(id) === index;
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function existingModels
 * @desc     Returns a function that checks if a given ID exists in a model map.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param map - The model map to check against.
 *
 * @returns A function that takes an ID and returns true if the ID exists
 *          in the map, false otherwise.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
function existingModels<T>(map: ModelMap<T>) {
  return (id: string) => map[id] === undefined;
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  * @function chunk
  * @desc     Returns a function that can be used to chunk an array into
  *           smaller arrays of a specified size.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param chunkSize The size of each chunk.
 *
 * @returns A function that takes an array of items, an item to add to the array,
 *          and the index of the item, and returns the chunked array.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
function chunk<T>(chunkSize: number) {
  return (chunkList: T[][], item: T, index: number) => {
    const chunkIndex = Math.floor(index / chunkSize);

    if (chunkList[chunkIndex] === undefined) {
      chunkList[chunkIndex] = [];
    }

    chunkList[chunkIndex].push(item);

    return chunkList;
  };
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function fetchNestedLtks
 * @desc     Fetches nested LTKs from the store by their IDs in batches.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param store - The Vuex store instance.
 * @param ltkIds - An array of LTK IDs to fetch.
 *
 * @returns A Promise that resolves when all LTKs have been fetched.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
async function fetchNestedLtks(store: Store<unknown>, ltkIds: string[]) {
  const chunkSize = 36;
  const ltkStore = getLtksStore(store);

  const promises = ltkIds
    .filter(Boolean)
    // Filter out profiles that already exist in the store
    .filter(existingModels(ltkStore.ltks))
    // Deduplicate list of ids
    .filter(duplicates)
    // Batch models into reasonable chunk suzes for batch requesting
    .reduce(chunk(chunkSize), [])
    // Create many batch requests
    .map((ids) => ltkStore.fetchLtksByIds(ids));

  // Bulk fetch missing profiles
  await Promise.all(promises);
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function fetchNestedProfiles
 * @desc     Fetches nested profiles from the store by their IDs in batches.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param store - The Vuex store instance.
 * @param profileIds - An array of profile IDs to fetch.
 *
 * @returns A Promise that resolves when all profiles have been fetched.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
async function fetchNestedProfiles(store: Store<unknown>, profileIds: string[]) {
  const chunkSize = 36;
  const profilesStore = getProfilesStore(store);

  const promises = profileIds
    .filter(Boolean)
    // Filter out profiles that already exist in the store
    .filter(existingModels(profilesStore.profiles))
    // Deduplicate list of ids
    .filter(duplicates)
    // Batch models into reasonable chunk suzes for batch requesting
    .reduce(chunk(chunkSize), [])
    // Create many batch requests
    .map((ids) => profilesStore.fetchProfilesByIds({ ids }));

  // Bulk fetch missing profiles
  await Promise.all(promises);
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function fetchNestedProductDetails
 * @desc     Fetches nested product details from the store by their IDs in batches.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @param store - The Vuex store.
 * @param productDetailsIds - An array of product detail IDs to fetch.
 *
 * @returns A Promise that resolves when all product details have been fetched.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
async function fetchNestedProductDetails(store: Store<unknown>, productDetailsIds: string[]) {
  const chunkSize = 48;
  const productDetailsStore = getProductDetailsStore(store);

  const promises = productDetailsIds
    .filter(Boolean)
    // Filter out profiles that already exist in the store
    .filter(existingModels(productDetailsStore.productDetails))
    // Deduplicate list of ids
    .filter(duplicates)
    // Batch models into reasonable chunk suzes for batch requesting
    .reduce(chunk(chunkSize), [])
    // Create many batch requests
    .map((ids) => productDetailsStore.fetchProductDetailsByIds({ ids }));

  // Bulk fetch missing models
  await Promise.all(promises);
}

export async function processCoreModelResponse(
  store: Store<unknown>,
  data: MappableResponse,
  fetchNestedModels = false,
) {
  const fetchedAt = Date.now();

  if (isMediaObjectsResponse(data)) {
    const mediaObjectsStore = getMediaObjectsStore(store);
    const mediaObjectsData = data.mediaObjects ?? [];
    if (mediaObjectsData.length > 0) {
      mediaObjectsData.forEach((mediaObject) => {
        mediaObject.fetchedAt = fetchedAt;
      });

      const mediaObjects = mapData("id", mediaObjectsData);
      mediaObjectsStore.setMediaObjects({ mediaObjects });
    }
  }

  if (isProductDetailsResponse(data)) {
    const productDetailsStore = getProductDetailsStore(store);
    const productDetailsData = data.productDetails ?? [];
    if (productDetailsData.length > 0) {
      productDetailsData.forEach((productDetail) => {
        productDetail.fetchedAt = fetchedAt;
      });

      const productDetails = mapData("id", productDetailsData);
      productDetailsStore.setProductDetails({ productDetails });
    }
  }

  if (isRetailerProductResponse(data)) {
    const retailerProductsStore = getRetailerProductsStore(store);
    const retailerProductData = data.retailerProduct;
    if (retailerProductData) {
      retailerProductData.fetchedAt = fetchedAt;
      const retailerProducts = { [retailerProductData.id]: retailerProductData };
      retailerProductsStore.setRetailerProducts({ retailerProducts });
    }
  }

  if (isProductsResponse(data)) {
    const productsStore = getProductsStore(store);
    const productsData = data.products ?? [];
    if (productsData.length > 0) {
      productsData.forEach((product) => {
        product.fetchedAt = fetchedAt;

        // Reduce data payload by trimming unnecessary data field
        if (product.hyperlink) {
          delete product.links.ltkWeb;
        }
      });

      if (fetchNestedModels) {
        const productDetailsIds = productsData.map((product) => product.productDetailsId);
        await fetchNestedProductDetails(store, productDetailsIds);
      }

      const products = mapData("id", productsData);
      productsStore.setProducts({ products });
    }
  }

  if (isProfilesResponse(data)) {
    const profilesStore = getProfilesStore(store);
    const profilesData = data.profiles ?? [];
    if (profilesData.length > 0) {
      profilesData.forEach((profile) => {
        profile.fetchedAt = fetchedAt;
      });
      const profiles = mapData("id", profilesData);
      profilesStore.setProfiles({ profiles });
    }
  }

  if (isFollowsResponse(data)) {
    const followsData = data.follows ?? [];
    if (followsData.length > 0) {
      followsData.forEach((follow) => {
        follow.fetchedAt = fetchedAt;
      });

      const follows = mapData("profileId", followsData);
      const followerStore = getFollowerStore(store);
      followerStore.setFollowedProfiles({ follows });
    }
  }

  // Set parent-most models last
  if (isLtksResponse(data)) {
    const ltksStore = getLtksStore(store);
    const ltksData = data.ltks ?? [];
    if ((ltksData ?? []).length > 0) {
      if (fetchNestedModels) {
        const profileIds = ltksData.map((ltk) => ltk.profileId);
        await fetchNestedProfiles(store, profileIds);
      }
      ltksData.forEach((ltk) => {
        ltk.fetchedAt = fetchedAt;
      });
      const ltks = mapData("id", ltksData);
      ltksStore.setLtks({ ltks });
    }
  }

  if (isLikesResponse(data)) {
    const likesStore = getLikesStore(store);
    const likesData = data.likes ?? [];
    if (likesData.length > 0) {
      likesData.forEach((like) => {
        like.fetchedAt = fetchedAt;
      });

      const likes = mapData("ltkId", likesData);
      likesStore.setLikes({ likes });
    }
  }

  if (isWishlistResponse(data)) {
    const wishelistsStore = getWishlistsStore(store);
    const wishesData = data.wishes ?? [];
    let wishes: { [key: string]: Wish };

    if (wishesData.length > 0) {
      wishesData.forEach((wish) => {
        wish.fetchedAt = fetchedAt;
      });

      if (fetchNestedModels) {
        const [ltkIds, productIds] = wishesData.reduce(
          (results: [string[], string[]], { ltkId, productId }) => {
            results[0].push(ltkId);
            results[1].push(productId);
            return results;
          },
          [[], []],
        );
        await Promise.all([fetchNestedLtks(store, ltkIds), fetchNestedProductDetails(store, productIds)]);

        wishes = mapData(
          "id",
          wishesData.filter((wish) => getProductDetailsStore(store).productDetails[wish.productId]),
        );
      } else {
        wishes = mapData("id", wishesData);
      }

      // shared wishlist come with ltks sideload, when favorite products doesn't
      if (data.ltks?.length) {
        getSharedWishlistsStore(store).setSharedCollectionWishes({ wishes });
      } else {
        wishelistsStore.setWishes({ wishes });
      }
    }
  }

  if (isCollectionsResponse(data)) {
    // Collection response includes Collections, an array of Collection Items for the related collections latestItems, and a full LTK request response.
    const collectionsStore = getCollectionsStore(store);
    const collectionsData = data.collections ?? [];

    if (collectionsData.length > 0) {
      if (data.latestItems) {
        const latestItems = mapData("itemId", data.latestItems);
        // Process collections
        collectionsData.forEach((collection) => {
          collection.fetchedAt = fetchedAt;
          collection.latestItemMap = {};
          collection.itemMap = {};

          collection?.latestItemIds?.forEach((id) => {
            collection.latestItemMap[id] = latestItems[id];
          });
        });
      }
      const collections = mapData("collectionId", collectionsData);
      collectionsStore.setManyCollectionInfo({ collections });

      // Processes fetched ltks
      const ltksData = data.postsV2 || {};
      await processCoreModelResponse(store, ltksData);
    }
  }

  if (isLocalizedCategoryResponse(data)) {
    const giftGuideStore = getGiftGuidesStore(store);
    if (data?.recipients && data?.occasions) {
      giftGuideStore.setLocalizedCategories({
        recipients: mapData(
          "key",
          data?.recipients.filter((item) => item.status === "ACTIVE"),
        ),
        occasions: mapData(
          "key",
          data?.occasions.filter((item) => item.status === "ACTIVE"),
        ),
      });
    }
  }

  if (isProductSetInfoResponse(data)) {
    const { shopProductCollection } = data;
    shopProductCollection.fetchedAt = fetchedAt;
    getProductSetsStore(store).setProductSet({
      productSetId: shopProductCollection.collectionId,
      productSet: shopProductCollection,
    });

    if (shopProductCollection.subtype === "GIFT_GUIDE") {
      getGiftGuidesStore(store).setGiftGuide({
        giftGuideId: shopProductCollection.collectionId,
        giftGuide: shopProductCollection as GiftGuide,
      });
    }
  }

  if (isProductSetsResponse(data)) {
    const { shopProductCollections } = data;

    if (shopProductCollections?.length) {
      shopProductCollections.forEach((product) => {
        product.fetchedAt = fetchedAt;
      });

      getProductSetsStore(store).setManyProductSets({ productSets: mapData("collectionId", shopProductCollections) });

      if (shopProductCollections[0].subtype === "GIFT_GUIDE") {
        getGiftGuidesStore(store).setGiftGuides({
          giftGuides: mapData("collectionId", shopProductCollections) as GiftGuideMap,
        });
      }
    }
  }

  if (isProductSetProductsResponse(data)) {
    const shopProducts = data.shopProducts;

    if (shopProducts?.length) {
      shopProducts.forEach((productSet) => {
        productSet.fetchedAt = fetchedAt;
      });

      getProductSetsStore(store).setProductSetProducts({
        productSetProducts: mapData("shopProductId", data.shopProducts),
      });

      if (shopProducts[0]?.linkUrl.toUpperCase().includes("GIFT_GUIDE")) {
        getGiftGuidesStore(store).setGiftGuideProducts({
          giftGuideProducts: mapData("shopProductId", shopProducts) as GiftGuideProductsMap,
        });
      }
    }
  }

  // TODO: update this once we fully implement posts and mediaObjects stores when moving to /activities/v3 for ltks
  if (isTrendingPostsResponse(data) || isGiftGuidesResponse(data)) {
    const trendingPostStore = getTrendingPostsStore(store);
    const posts = data.posts as TrendingPost[];
    const mediaObjects =
      "mediaObjects" in data ? data.mediaObjects : "coverPhotos" in data && data.coverPhotos ? data.coverPhotos : [];
    const mediaObjectMap = mapData("id", mediaObjects);

    if (posts?.length && mediaObjects?.length) {
      posts.forEach((post) => {
        post.media = (post.mediaObjectIds ?? post.coverPhotoIds)
          ?.map((mediaObjectId: string) => mediaObjectMap[mediaObjectId])
          .filter(filterRemoveUndefined);
        post.fetchedAt = fetchedAt;
      });

      trendingPostStore.setTrendingPosts(mapData("id", posts));
    }
  }
}

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function getModuleFromContext
 * @deprecated Switch to Pinia.
 *
 * @desc     Helper function to retrieve a vuex module from the store
 *           when a store instance is not available, for example, from
 *           within a store getter. This mimics some behavior provided
 *           by `vuex-module-decorators` `getModule` function. It will,
 *           however, purposely return undefined instead of a detached
 *           store when a module could not be found to avoid confusion
 *           as to what capabilities are available on the store, like
 *           exposed services.
 *
 * @summary  retrieves a vuex module from the store
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @template T - The type of the vuex module.
 *
 * @param context - The vuex action context.
 * @param moduleName - The name of the vuex module to retrieve.
 *
 * @returns The vuex module if it exists, undefined otherwise.
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @example <caption>usage of `getModuleFromContext`</caption>
 * ```ts
 * import { getModuleFromContext } from "@/store/-utils";
 * import { getProfilesStore } from "@/store/profiles";
 *
 * const profilesStore = getModuleFromContext(getProfilesStore(store), "profiles");
 * ```
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const getModuleFromContext = <T extends VuexModule>(
  context: ActionContext<unknown, unknown>,
  moduleName: string,
) => {
  return context.rootGetters[`vuexModuleDecorators/${moduleName}`] as T | undefined;
};
